import { getDefaultConfig } from '@rainbow-me/rainbowkit';
import {
  baseAccount,
  coinbaseWallet,
  rabbyWallet,
} from '@rainbow-me/rainbowkit/wallets';
import { metaMaskSdkOnlyWallet } from './wallets/metaMaskSdkOnlyWallet';
import { arbitrum, avalanche, base, bsc, gnosis, mainnet, optimism, polygon, sepolia } from 'wagmi/chains';
import { defineChain, http } from 'viem';
import { getChainIconSrc } from './config/chainDisplay';
import { brand } from './config/brand';

/** RainbowKit merges `iconUrl` / `iconBackground` into the chain switcher + ConnectButton. */
export const pulsechain = Object.assign(
  defineChain({
    id: 369,
    name: 'PulseChain',
    nativeCurrency: { name: 'Pulse', symbol: 'PLS', decimals: 18 },
    rpcUrls: { default: { http: ['https://rpc.pulsechain.com'] } },
    blockExplorers: { default: { name: 'PulseScan', url: 'https://scan.pulsechain.com' } },
  }),
  {
    iconUrl: getChainIconSrc(369),
    iconBackground: '#161616',
  }
);

export const linea = Object.assign(
  defineChain({
    id: 59144,
    name: 'Linea',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: { default: { http: ['https://rpc.linea.build'] } },
    blockExplorers: { default: { name: 'LineaScan', url: 'https://lineascan.build' } },
  }),
  { iconUrl: getChainIconSrc(59144), iconBackground: '#121212' }
);

export const abstract = Object.assign(
  defineChain({
    id: 2741,
    name: 'Abstract',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: { default: { http: ['https://api.mainnet.abs.xyz'] } },
    blockExplorers: { default: { name: 'ABScan', url: 'https://abscan.org' } },
  }),
  { iconUrl: getChainIconSrc(2741), iconBackground: '#121212' }
);

export const monad = Object.assign(
  defineChain({
    id: 143,
    name: 'Monad',
    nativeCurrency: { name: 'Monad', symbol: 'MON', decimals: 18 },
    rpcUrls: { default: { http: ['https://rpc.monad.xyz'] } },
    blockExplorers: { default: { name: 'Monad Explorer', url: 'https://explorer.monad.xyz' } },
  }),
  { iconUrl: getChainIconSrc(143), iconBackground: '#121212' }
);

export const scroll = Object.assign(
  defineChain({
    id: 534352,
    name: 'Scroll',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: { default: { http: ['https://rpc.scroll.io'] } },
    blockExplorers: { default: { name: 'Scrollscan', url: 'https://scrollscan.com' } },
  }),
  { iconUrl: getChainIconSrc(534352), iconBackground: '#121212' }
);

export const unichain = Object.assign(
  defineChain({
    id: 130,
    name: 'Unichain',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: { default: { http: ['https://mainnet.unichain.org'] } },
    blockExplorers: { default: { name: 'UniScan', url: 'https://uniscan.xyz' } },
  }),
  { iconUrl: getChainIconSrc(130), iconBackground: '#121212' }
);

/**
 * RainbowKit requires `projectId` on getDefaultConfig. We omit the generic `injectedWallet`
 * (“Browser Wallet”) so Phantom does not own EVM `wallet_switchEthereumChain`.
 *
 * Stock `metaMaskWallet` only uses the MetaMask SDK when `isMetaMask(window.ethereum)`;
 * with Phantom as the default provider it falls back to WalletConnect and a placeholder
 * `projectId`, which never completes. `metaMaskSdkOnlyWallet` always uses wagmi’s
 * `metaMask()` connector so the extension is found via the SDK / EIP-6963.
 */
const unusedWalletConnectProjectIdPlaceholder =
  process.env.REACT_APP_WALLETCONNECT_PROJECT_ID ?? '00000000000000000000000000000000';

export const wagmiConfig = getDefaultConfig({
  appName: brand.wagmiAppName,
  projectId: unusedWalletConnectProjectIdPlaceholder,
  chains: [
    mainnet,
    bsc,
    base,
    arbitrum,
    polygon,
    avalanche,
    optimism,
    gnosis,
    unichain,
    linea,
    abstract,
    monad,
    scroll,
    pulsechain,
    sepolia,
  ],
  transports: {
    [mainnet.id]: http(),
    [bsc.id]: http(),
    [base.id]: http(),
    [arbitrum.id]: http(),
    [polygon.id]: http(),
    [avalanche.id]: http(),
    [optimism.id]: http(),
    [gnosis.id]: http(),
    [unichain.id]: http(),
    [linea.id]: http(),
    [abstract.id]: http(),
    [monad.id]: http(),
    [scroll.id]: http(),
    [pulsechain.id]: http(),
    [sepolia.id]: http(),
  },
  ssr: false,
  wallets: [
    {
      groupName: 'Connect',
      wallets: [metaMaskSdkOnlyWallet, rabbyWallet, coinbaseWallet, baseAccount],
    },
  ],
});
